import { useCallback, useMemo } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Buy targets and their trade-group matches, from /api/core-list/targets.
//
// A separate call from /api/core-list on purpose: the grouped-tree endpoint is
// shared with the banner and the KPI strip, and a store with no targets should
// pay nothing for this. It joins onto the tree by core_list_item_id, the same
// way useCoreListDemand joins by product_id.
const fetchTargets = async (storeIdM) => {
  const { data } = await axios.get(`/api/core-list/targets`, {
    params: { store_id_m: storeIdM },
  });
  return data;
};

/**
 * useCoreListTargets — per-reference buy targets and the auto-approved
 * trade-group prices that meet them.
 *
 * @param {number|null} storeIdM
 * @returns {{
 *   targetsByItemId: Map<number, object>,
 *   baseCurrency: string,
 *   totalHits: number,
 *   referencesAtTarget: number,
 *   isLoading: boolean,
 *   isError: boolean,
 *   refetchTargets: () => void,
 * }}
 */
// `enabled` = false when the store lacks Stock alert (it may still have the
// Arbitrages tab), so the 403 is never asked for.
export const useCoreListTargets = (storeIdM, enabled = true) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["core-list-targets", storeIdM], [storeIdM]);

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => fetchTargets(storeIdM),
    enabled: enabled && storeIdM != null,
    staleTime: 0,
  });

  const refetchTargets = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => {
    const targetsByItemId = new Map();
    let totalHits = 0;
    let referencesAtTarget = 0;

    // The backend returns `items` as an object keyed by core_list_item_id (it
    // is cast to an object server-side so an empty result is `{}`, not `[]`).
    Object.entries(data?.items || {}).forEach(([itemId, entry]) => {
      targetsByItemId.set(Number(itemId), entry);

      // Trade-group quotes AND marketplace listings (Alex, 2026-10-08), AND
      // live auction lots under target (Stock Alert, 2026-11-02). The KPI
      // tile and the filter pills are counted in two different places — here
      // and in CoreListTable — so BOTH had to learn each source, or the tile
      // would say 0 while the pill beside it said 4.
      const hits =
        (Number(entry?.hit_count) || 0) +
        (Number(entry?.marketplace?.match_count) || 0) +
        (Number(entry?.auction?.match_count) || 0);

      totalHits += hits;
      if (hits > 0) referencesAtTarget += 1;
    });

    return {
      targetsByItemId,
      baseCurrency: data?.base_currency || "gbp",
      totalHits,
      referencesAtTarget,
      isLoading,
      isError,
      refetchTargets,
    };
  }, [data, isLoading, isError, refetchTargets]);
};

export default useCoreListTargets;
